import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronDown, Check, TrendingUp, Wind } from 'lucide-react';
import { Emoji } from '@/components/Emoji';
import { useTelegram } from '@/hooks/useTelegram';
import { CheckinRelaxSuggestion } from '@/components/CheckinRelaxSuggestion';
import { useCheckin, localDate } from '@/hooks/useCheckin';
import { useUserStore } from '@/store/userStore';
import { enableTrend } from '@/lib/moodTrend';

// ════════════════════════════════════════════════════════════════
// Отметка дня. По умолчанию — одна тонкая строка с пульсирующим мозгом,
// верх главной остаётся свободным. Раскрывается по тапу.
// Утром спрашиваем только сон (пока он точный), вечером с 19:30 — «как день»
// (до окна срывов: 31% последних приёмов приходится на 21:00–23:00,
// поэтому настроение, отмеченное раньше, работает предсказателем).
// ════════════════════════════════════════════════════════════════

type Level = { v: number; e: string; label: string; desc: string };

const SLEEP: Level[] = [
  { v: 1, e: '😵', label: 'Ночь мимо', desc: 'Сна почти не было' },
  { v: 2, e: '😖', label: 'Урывками', desc: 'Просыпался(лась), беспокойно' },
  { v: 3, e: '😐', label: 'Средне', desc: 'Поспал(а), но бодрости нет' },
  { v: 4, e: '🙂', label: 'Выспался(лась)', desc: 'Утром были силы' },
  { v: 5, e: '😃', label: 'Отлично', desc: 'Всю ночь без пробуждений, утром свежесть' },
];

const DAY: Level[] = [
  { v: 1, e: '😞', label: 'Выжат(а)', desc: 'Хотелось, чтобы он скорее закончился' },
  { v: 2, e: '😕', label: 'Тяжело', desc: 'Раздражало, тревожно, всё через силу' },
  { v: 3, e: '😐', label: 'Обычный', desc: 'Ни хорошо, ни плохо. Просто день' },
  { v: 4, e: '🙂', label: 'Хороший', desc: 'Спокойно, силы были' },
  { v: 5, e: '😃', label: 'Отличный', desc: 'Лёгкость, всё складывалось' },
];

const TAGS = ['работа', 'дедлайн', 'ссора', 'болел(а)', 'одиноко', 'праздник', 'тренировка', 'спокойно'];

function Scale({
  title,
  levels,
  value,
  onPick,
}: {
  title: string;
  levels: Level[];
  value: number | null;
  onPick: (v: number) => void;
}) {
  const [showDesc, setShowDesc] = useState(false);
  const { haptic } = useTelegram();

  return (
    <div>
      <div className="text-sm font-medium mb-2">{title}</div>

      <div className="grid grid-cols-5 gap-1.5">
        {levels.map((l) => (
          <button
            key={l.v}
            onClick={() => {
              haptic('light');
              onPick(l.v);
            }}
            className={[
              'py-2 px-1 rounded-xl border flex flex-col items-center gap-1 transition-all active:scale-95',
              value === l.v ? 'bg-emerald-glow/15 border-emerald-glow/50' : 'bg-bg-card border-line',
            ].join(' ')}
          >
            <span className="text-lg leading-none">{l.e}</span>
            <span className="text-[10px] leading-tight text-ink-muted text-center">{l.label}</span>
          </button>
        ))}
      </div>

      <button
        onClick={() => setShowDesc((o) => !o)}
        className="mt-2 flex items-center gap-1 text-[11px] text-ink-dim active:opacity-70"
      >
        что значат оценки
        <ChevronDown size={12} className={showDesc ? 'rotate-180 transition-transform' : 'transition-transform'} />
      </button>

      {showDesc && (
        <div className="mt-2 space-y-1">
          {levels.map((l) => (
            <div key={l.v} className="text-[11px] text-ink-dim leading-snug">
              <span className="text-ink-muted">
                {l.e} {l.label}
              </span>{' '}
              — {l.desc}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DailyCheckinCard() {
  const {
    today,
    yesterday,
    loading,
    askSleep,
    askDay,
    canBackfillYesterday,
    filledDays,
    saveSleep,
    saveDay,
    save,
  } = useCheckin();

  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const [tags, setTags] = useState<string[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [backfill, setBackfill] = useState(false);
  const { haptic } = useTelegram();
  const reduced = useReducedMotion();
  const nav = useNavigate();
  const { profile, setProfile } = useUserStore();
  const [trendJustOn, setTrendJustOn] = useState(false);
  const tracking = !!profile?.trend_tracking_at;

  // «Отслеживать тренд» (10.10.2026): человек сам включает — и понимает, зачем
  // отмечает. Сразу после включения предлагаем релакс как рычаг, которым линию двигают.
  async function turnTrendOn() {
    if (!profile?.id) return;
    haptic('medium');
    const at = await enableTrend(profile.id);
    if (at) {
      setProfile({ ...profile, trend_tracking_at: at });
      setTrendJustOn(true);
    }
  }

  if (loading) return null;

  const needsAction = askSleep || askDay;
  const done = !!today?.mood;
  // Окно закрыто и отмечать нечего — не занимаем верх экрана вообще
  // Включившему тренд строка видна всегда: через неё вход в «Мой месяц»
  // Прячем строку, только когда сон уже отмечен и до вечера спрашивать нечего:
  // пока сон не отмечен, возможность отметить должна быть под рукой весь день.
  if (!needsAction && !done && !tracking && today?.sleep) return null;

  // Черновик поверх сохранённого: порядок действий ничего не теряет
  const draftTags = tags ?? today?.tags ?? [];
  const draftNote = note ?? today?.note ?? '';

  function pushExtra(next: { tags?: string[]; note?: string | null }) {
    if (!today?.mood) return;
    save(localDate(), next);
  }

  const left = 7 - filledDays;
  const beacon =
    filledDays >= 7
      ? 'неделя собрана — в воскресенье большой разбор'
      : `${filledDays} из 7 · до полного разбора ${left} ${left === 1 ? 'отметка' : left < 5 ? 'отметки' : 'отметок'}`;

  const title = askDay ? 'Отметь день' : !today?.sleep ? 'Как спал(а)?' : done ? 'День отмечен' : 'Сон отмечен — про день спрошу вечером';
  // Мозг светится, пока на него не нажали. Тапнул — свечение гаснет до конца сеанса,
  // чтобы не мигать на человека, который уже пришёл.
  const pulsing = !open && !everOpened && !reduced;

  return (
    <div
      className={[
        'w-full mb-3 rounded-2xl overflow-hidden transition-colors duration-300',
        // Пока свёрнуто — никакой коробки, строка просто лежит на фоне
        open ? 'bg-bg-card border border-line' : 'border border-transparent',
      ].join(' ')}
    >
      {/* Свёрнутая строка — то, что видно по умолчанию */}
      <button
        onClick={() => {
          haptic('light');
          setEverOpened(true);
          setOpen((o) => !o);
        }}
        className="w-full px-1.5 py-1.5 flex items-center gap-2.5 text-left active:opacity-80"
      >
        <div className="relative w-9 h-9 rounded-full bg-emerald-glow/15 flex items-center justify-center shrink-0">
          {/* Свечение вынесено отдельным слоем: анимируются только прозрачность
              и масштаб — их считает видеокарта. Раньше по кадрам менялся
              box-shadow, а это полная перерисовка каждый кадр, и на скролле
              она складывалась с блюром карточек в заметные тормоза.
              Сама тень статична, поэтому растрируется один раз. */}
          {pulsing && (
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{ boxShadow: '0 0 22px rgba(16,229,142,0.65)' }}
              initial={{ opacity: 0.4, scale: 1 }}
              animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.1, 1] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}
          {/* Мозг виден всегда — это опознавательный знак раздела */}
          <Emoji e="🧠" className="w-[19px] h-[19px] relative" />
          {done && (
            <span className="absolute -right-0.5 -bottom-0.5 w-3.5 h-3.5 rounded-full bg-bg flex items-center justify-center">
              <Check size={10} className="text-emerald-glow" />
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className={['text-sm truncate', needsAction ? 'font-medium' : 'text-ink-muted'].join(' ')}>{title}</div>
          <div className="text-[10px] text-ink-dim truncate">{beacon}</div>
        </div>

        {/* Маяк обещает разбор — значит на него должно быть куда нажать.
            Ссылка появляется, как только разбору есть что показать. */}
        {tracking && (
          <span
            role="link"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              haptic('light');
              nav('/trend');
            }}
            className="shrink-0 text-[10px] text-emerald-glow px-2 py-1 rounded-lg bg-emerald-glow/10 active:opacity-70 flex items-center gap-1"
          >
            <TrendingUp size={11} /> Тренд
          </span>
        )}
        {!tracking && filledDays >= 3 && (
          <span
            role="link"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              haptic('light');
              nav('/week-review');
            }}
            className="shrink-0 text-[10px] text-emerald-glow px-2 py-1 rounded-lg bg-emerald-glow/10 active:opacity-70"
          >
            Разбор
          </span>
        )}

        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0 pr-1">
          <ChevronDown size={16} className="text-ink-dim" />
        </motion.span>
      </button>

      {/* Раскрывающаяся часть */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="px-3 pb-3 pt-1 space-y-3">
              {backfill && canBackfillYesterday ? (
                <Scale
                  title="Как прошёл вчерашний день?"
                  levels={DAY}
                  value={yesterday?.mood ?? null}
                  onPick={(v) => {
                    saveDay(v, undefined, localDate(-1));
                    setBackfill(false);
                  }}
                />
              ) : (
                <>
                  {/* Сон можно отметить в любое время дня (12.10.2026). Раньше шкала
                      показывалась только до 12:00 и после 19:30, а строка у включивших
                      тренд видна всегда — днём она раскрывалась пустой, нажать было не на что. */}
                  {(
                    <Scale
                      title="Как спал(а)?"
                      levels={SLEEP}
                      value={today?.sleep ?? null}
                      onPick={saveSleep}
                    />
                  )}

                  {!askDay && !done && (
                    <p className="text-[11px] text-ink-dim">
                      Про то, как прошёл день, спрошу после 19:30 — к вечеру оценка точнее.
                    </p>
                  )}

                  {(askDay || done) && (
                    <>
                      <Scale
                        title="Как прошёл день?"
                        levels={DAY}
                        value={today?.mood ?? null}
                        onPick={(v) => saveDay(v, { tags: draftTags, note: draftNote.trim() || null })}
                      />

                      {/* Теги и пара слов необязательны, но именно они объясняют «почему» */}
                      <div className="flex flex-wrap gap-1.5">
                        {TAGS.map((t) => (
                          <button
                            key={t}
                            onClick={() => {
                              haptic('light');
                              const next = draftTags.includes(t)
                                ? draftTags.filter((x) => x !== t)
                                : draftTags.length >= 3
                                  ? draftTags
                                  : [...draftTags, t];
                              setTags(next);
                              pushExtra({ tags: next });
                            }}
                            className={[
                              'px-2.5 py-1 rounded-full border text-[11px] transition-all',
                              draftTags.includes(t)
                                ? 'bg-emerald-glow/15 border-emerald-glow/50 text-ink'
                                : 'bg-bg-card border-line text-ink-muted',
                            ].join(' ')}
                          >
                            {t}
                          </button>
                        ))}
                      </div>

                      <input
                        value={draftNote}
                        onChange={(e) => setNote(e.target.value.slice(0, 280))}
                        onBlur={() => pushExtra({ note: draftNote.trim() || null })}
                        placeholder="Пара слов — если хочется"
                        className="w-full bg-bg-card border border-line rounded-xl px-3 py-2 text-sm placeholder:text-ink-dim outline-none focus:border-emerald-glow/50"
                      />

                      {/* Практика под то, что человек ТОЛЬКО ЧТО отметил.
                          До этого отметка «тяжёлый день» никуда не вела:
                          человек говорил, что ему плохо, и не получал ничего.
                          Показывается только когда день правда был тяжёлым —
                          предлагать дыхание при «отличном дне» значит
                          превратить заботу в фоновый шум. */}
                      <CheckinRelaxSuggestion
                        mood={today?.mood}
                        sleep={today?.sleep}
                        tags={draftTags}
                      />
                    </>
                  )}

                  {canBackfillYesterday && (
                    <button
                      onClick={() => {
                        haptic('light');
                        setBackfill(true);
                      }}
                      className="text-[11px] text-ink-dim underline underline-offset-2 active:opacity-70"
                    >
                      отметить вчерашний день
                    </button>
                  )}

                  {/* Зачем отмечать и куда потом смотреть — ответ прямо здесь */}
                  {!tracking && (today?.sleep || today?.mood) && (
                    <button
                      onClick={turnTrendOn}
                      className="w-full p-3 rounded-xl border border-emerald-glow/30 bg-emerald-glow/5 text-left active:scale-[0.99] transition-transform"
                    >
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <TrendingUp size={15} className="text-emerald-glow" /> Отслеживать тренд
                      </div>
                      <div className="text-[11px] text-ink-dim mt-0.5 leading-snug">
                        Соберу отметки в график месяца: увидишь, куда идут сон и настроение и от чего они зависят
                      </div>
                    </button>
                  )}

                  {trendJustOn && (
                    <div className="p-3 rounded-xl border border-sky-glow/25 bg-sky-glow/5">
                      <div className="text-sm font-medium">Тренд включён</div>
                      <div className="text-[11px] text-ink-dim mt-0.5 leading-snug">
                        Сегодняшняя отметка уже на графике. Хочешь, чтобы линия шла вверх? Начни с двух-трёх минут дыхания вечером.
                      </div>
                      <div className="flex gap-2 mt-2.5">
                        <button
                          onClick={() => { haptic('medium'); nav('/breathing/box'); }}
                          className="flex-1 py-2 rounded-lg bg-sky-glow/15 border border-sky-glow/30 text-[11px] font-medium flex items-center justify-center gap-1.5"
                        >
                          <Wind size={13} /> Дыхание, 3 минуты
                        </button>
                        <button
                          onClick={() => { haptic('light'); nav('/trend'); }}
                          className="flex-1 py-2 rounded-lg bg-bg-card border border-line text-[11px] text-ink-muted"
                        >
                          Мой месяц
                        </button>
                      </div>
                    </div>
                  )}

                  {done && (
                    <button
                      onClick={() => {
                        haptic('light');
                        pushExtra({ tags: draftTags, note: draftNote.trim() || null });
                        setOpen(false);
                      }}
                      className="w-full py-2 rounded-xl bg-emerald-glow/15 border border-emerald-glow/50 text-[12px] active:scale-[0.99] transition-transform"
                    >
                      Готово
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
